import React, { HTMLAttributes, ReactNode, useState } from 'react';
import classes from './PlanContainer.module.scss';
import { Typography } from 'antd';
import { RxCaretDown } from 'react-icons/rx';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { colorToHex } from 'shared/libs/utils/helpers';
import DualBalance from 'shared/ui/DualBalance/DualBalance';
import clsx from 'clsx';
import ActivityIndicator from '../ActivityIndicator/ActivityIndicator';
import { AddAccount } from 'features/account/add-account';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { RootState } from 'shared/config/store/store';
import { VerificationStatus } from 'entities/user/model/types';
import { EMI } from 'shared/model/types';

interface PlanContainerProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  active: boolean;
  availBal?: number;
  loading?: boolean;
  disabled?: boolean;
  available?: boolean;
  items?: Array<object>;
  renderItems?: (item: any, secured?: boolean) => React.ReactNode;
  footer?: ReactNode | (() => ReactNode);
  id?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => any;
  secured?: boolean;
  setSecured: (s: boolean) => void;
  canAddAccount?: boolean;
}
const PlanContainer: React.FC<PlanContainerProps> = ({
  title,
  footer,
  renderItems,
  items,
  active,
  available,
  disabled,
  loading,
  availBal,
  onClick,
  id,
  secured,
  setSecured,
  canAddAccount,
  ...props
}) => {
  const [expanded, setExpanded] = useState(false);
  const { formatMessage: t } = useIntl();
  const hasBalance = availBal !== undefined && availBal !== null;
  const { verificationStatus } = useSelector((state: RootState) => getUserMeta(state));
  const isVerified = [
    VerificationStatus.Verified,
    VerificationStatus.Completed,
  ].includes(verificationStatus);  

  const onExpand = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <div {...props} className={clsx(classes.wrapper, props.className)}>
      <div
        id={id + '-plan-container'}
        onClick={active ? onExpand : onClick}
        className={classes.container}
        style={{
          background: `linear-gradient(135.00deg, rgb(193, 236, 241) 0%,rgb(195, 230, 246) 100%)`
        }}
      >
        <div className={classes.balanceSection}>
          <Typography.Text ellipsis className={classes.title}>
            {title.toUpperCase()}
          </Typography.Text>

          {active ? (
            <div className={classes.secondRow}>
              <div className={classes.head}>
                {hasBalance ? (
                  <DualBalance
                    ledgerBalance={availBal}
                    secured={secured}
                    size="lg"
                    idPrefix={secured ? 'plan-balance-hidden' : 'plan-balance-shown'}
                  />
                ) : (
                  <Typography.Text
                    className={classes.balance}
                    id={secured ? 'plan-balance-hidden' : 'plan-balance-shown'}
                  >
                    {secured ? '*' : '--'}
                  </Typography.Text>
                )}
                {isVerified && (
                  <div
                    style={{ zIndex: 9 }}
                    id={id ? `btn__${id}-show-balance` : ''}
                    className={clsx(classes.caretIcon, classes.headAction)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSecured(!secured);
                    }}
                  >
                    {secured ? (
                      <EyeOutlined className={classes.icon} />
                    ) : (
                      <EyeInvisibleOutlined className={classes.icon} />
                    )}
                  </div>
                )}
              </div>
              {items && items.length > 0 && (
                <div
                  onClick={onExpand}
                  className={clsx(classes.caretIcon, expanded && classes.expanded)}
                >
                  <RxCaretDown />
                </div>
              )}
            </div>
          ) : null}
        </div>

        {active ? null : (
          <div className={classes.actionsContainer}>
            <AddAccount type='button' />
          </div>
        )}

        {footer ? (typeof footer === 'function' ? footer() : footer) : null}

        {(disabled || loading || available === false) && (
          <div className={classes.disabledOverlay}>
            {available === false ? (
              <div className={classes.notAvailableContainer}>
                <Typography.Text className={classes.notAvailableText}>
                  {t({ id: 'plan.notAvailable' })}
                </Typography.Text>
              </div>
            ) : (
              <>
                {loading && (
                  <div className={classes.loadingContainer}>
                    <div className={classes.indicatorContainer}>
                      <ActivityIndicator />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {items && items.length > 0 && (
        <div
          className={clsx(classes.itemsContainer, expanded && classes.expanded)}
          style={{ background: `${colorToHex('rgb(193, 236, 241)')}55` }}
        >
          {renderItems && renderItems(items, secured)}
        </div>
      )}
      {canAddAccount && (
        <div className={classes.actionsContainer}>
          <AddAccount type="container" />
        </div>
      )}
    </div>
  );
};

export default React.memo(PlanContainer);
